import { Box, Typography } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import {
    ReferenceInput,
    required,
    SimpleForm,
    useGetList,
    useNotify,
    useTranslate,
} from "react-admin";
import { RaCreateView } from "../../components/views/RaCreateView";
import { RaAutocompleteInput } from "../../components/inputs/Ra/RaAutocompleteInput";
import { RaTextInput } from "../../components/inputs/Ra/RaTextInput";
import { RaNumberInput } from "../../components/inputs/Ra/RaNumberInput";
import { RaDateInput } from "../../components/inputs/Ra/RaDateInput";
import { FileManagerContext } from "./FileManagerContext";
import { handleHttpError } from "shared/src/utils/errorHandler";

export const ArrivalForm = () => {
    const { data, scheduleUpdate, isCheckingArrivalForm, setIsCheckingArrivalForm } = useContext(FileManagerContext);
    const arrivalFormRef = useRef();
    const translate = useTranslate();
    const notify = useNotify();
    const hasSupplierId = typeof data?.arrivalData?.supplier === "number";

    useGetList(
        "suppliers",
        {
            filter: { name: data?.arrivalData?.supplier },
            pagination: {
                page: 1,
                perPage: 1,
            },
            sort: { field: "id", order: "DESC" },
        },
        {
            onSuccess: ({ data }) => {
                const { id } = data[0] || {};
                if (id) {
                    scheduleUpdate((prevData) => ({
                        ...prevData,
                        arrivalData: {
                            ...prevData.arrivalData,
                            supplier: id,
                        },
                    }));
                }
            },
            onError: (e) => {
                handleHttpError(e, notify);
            },
            enabled:
                !!data?.arrivalData?.supplier && typeof data?.arrivalData?.supplier === "string", // Only fetch if supplier is defined,
        }
    );

    const handleChange = ({ name, value }) => {
        scheduleUpdate((prevData) => ({
            ...prevData,
            arrivalData: {
                ...prevData.arrivalData,
                [name]: value,
            },
        }));
    };

    useEffect(() => {
        if (isCheckingArrivalForm && arrivalFormRef.current) {
            const form = arrivalFormRef.current.querySelector("form");
            if (form) {
                form.requestSubmit();
            }
            setIsCheckingArrivalForm(false);
        }
    }, [isCheckingArrivalForm, arrivalFormRef, setIsCheckingArrivalForm]);

    const handleSubmit = () => {
        return Promise.resolve();
    };

    return (
        <Box ref={arrivalFormRef}>
            <RaCreateView mutationMode="pessimistic">
                <Typography variant="h6" sx={{ p: 2 }}>
                    {translate("equip.arrivalInformation")}
                </Typography>
                <SimpleForm
                    onSubmit={handleSubmit}
                    onChange={(e) => {
                        const { name, value } = e.target;
                        if (name === "supplier.id") {
                            return;
                        }

                        handleChange({ name, value });
                    }}
                    toolbar={<></>}
                    record={{
                        ...data?.arrivalData,
                        ...(hasSupplierId && {
                            supplier: {
                                id: data?.arrivalData?.supplier,
                            },
                        }),
                    }}
                    sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
                >
                    <ReferenceInput
                        source="supplier.id"
                        reference="suppliers"
                        sort={{ field: "name", order: "ASC" }}
                    >
                        <RaAutocompleteInput
                            label="equip.supplier"
                            fullWidth
                            field="name"
                            onChange={(event, value) => {
                                handleChange({ name: "supplier", value: value?.id ?? null });
                            }}
                            validate={[required()]}
                        />
                    </ReferenceInput>
                    <RaTextInput label="equip.purchaseOrderNumber" source="purchaseOrderNumber" />
                    <RaNumberInput label="equip.purchasePrice" source="purchasePrice" />
                    <RaDateInput label="equip.orderDate" source="orderDate" />
                    <RaDateInput label="equip.deliveryDate" source="deliveryDate" />
                    <RaNumberInput label="equip.defectQuantity" source="defectQuantity" />
                    <RaTextInput label="equip.comment" source="comment" multiline rows={3} />
                </SimpleForm>
            </RaCreateView>
        </Box>
    );
};
